import type { CanvasEntity } from '../entityTypes';
import type { AlignmentGuide } from './alignmentGuides';
import { computeAlignmentSnap } from './alignmentGuides';
import { GRID_SIZE } from '../types';

export interface AlignedMovesResult {
  moves: Array<{ id: string; x: number; y: number }>;
  snapDx: number;
  snapDy: number;
  guides: AlignmentGuide[];
}

export function computeAlignedMoves(
  entityStarts: Array<{ id: string; startX: number; startY: number }>,
  dx: number,
  dy: number,
  draggedAlignIds: string[],
  allEntities: CanvasEntity[],
  entityMap: Map<string, CanvasEntity>,
  getEffectiveDims: (ent: { kind: string; width: number; height: number; id: string }) => { width: number; height: number },
): AlignedMovesResult {
  const moves = entityStarts.map(sp => ({
    id: sp.id,
    x: Math.round((sp.startX + dx) / GRID_SIZE) * GRID_SIZE,
    y: Math.round((sp.startY + dy) / GRID_SIZE) * GRID_SIZE,
  }));

  let snapDx = 0;
  let snapDy = 0;
  let guides: AlignmentGuide[] = [];

  if (draggedAlignIds.length > 0) {
    const draggedSet = new Set(draggedAlignIds);
    const movesMap = new Map(moves.map(m => [m.id, m]));

    // Static: all non-dragged entities with dimensions
    const staticPositions = allEntities
      .filter(ent => {
        if (draggedSet.has(ent.id)) return false;
        const dims = getEffectiveDims(ent);
        return dims.width > 0 && dims.height > 0;
      })
      .map(ent => {
        const dims = getEffectiveDims(ent);
        return { x: ent.x, y: ent.y, width: dims.width, height: dims.height };
      });

    // Dragged: use current move positions with effective dimensions
    const draggedPositions = draggedAlignIds
      .map(id => {
        const m = movesMap.get(id);
        const ent = entityMap.get(id);
        if (!m || !ent) return null;
        const dims = getEffectiveDims(ent);
        return { x: m.x, y: m.y, width: dims.width, height: dims.height };
      })
      .filter((p): p is NonNullable<typeof p> => p !== null);

    const snap = computeAlignmentSnap(draggedPositions, staticPositions);
    snapDx = snap.snapDx;
    snapDy = snap.snapDy;
    if (snapDx !== 0 || snapDy !== 0) {
      for (const m of moves) {
        m.x += snapDx;
        m.y += snapDy;
      }
    }
    guides = snap.guides;
  }

  return { moves, snapDx, snapDy, guides };
}
